// frontend/src/utils/authBoot.js
//
// Taking down the authenticated-boot splash (backlog #158) - the ONE module that
// removes it from the React side.
//
// The head script in public/index.html adds AUTH_BOOT_CLASS to <html> on a
// signed-in cold start of "/" (hint cookie `lf_auth=1`): #root is hidden and
// the branded wash + wordmark cover the viewport while React boots, the session
// is checked and the landing page's chunk downloads. It used to come down at
// React's FIRST commit, which on an installed PWA put two spinners between the
// splash and the dashboard (the auth gate's, then the route Suspense fallback).
// Now it stays up until the page has actually painted, then fades into it:
//
//   - releaseAuthBoot() - called by the first commit of a real page: AuthLayout's
//     (components/AuthLayout.js), and - for a protected page without one
//     (RecommenderDashboard, ...) - ProtectedRoute's ReleaseAuthBootOnMount,
//     which commits with the page inside its cold-boot boundary. Also by
//     ProtectedRoute's redirect to /login, once Login's chunk has settled;
//     and, for a PUBLIC page without AuthLayout (/unsubscribe, /enterprise/pay,
//     /verify-email, /supporter/..., an unknown address - every 404.html deep
//     link boots "/" and so raises the splash), by appRoutes.js's
//     ReleaseAuthBootOnPublicRoute, which App renders as the last child of its
//     route <Suspense> so it commits with that page - never on "/" (RootRoute
//     is redirecting) or on a protected or redirect-to-protected address;
//   - armAuthBootCap() - App's backstop: whatever happens, the splash comes down
//     at most AUTH_BOOT_CAP_MS after navigation start while React is alive (a
//     chunk crawling over slow 3G, a Login chunk that never settles - #root
//     then shows whatever loader is mounted).
//
// The head script's own 4s failsafe stays the hard bound for "React never
// mounted" (a failed main chunk, a JS error before render). Nothing here can
// replace it: this module only exists once the bundle has run.
//
// RESILIENCE: this runs on the boot path of every signed-in session, so every
// DOM, timer, matchMedia and performance access is guarded and degrades to
// "remove the class now". No outcome may leave #root hidden; nothing is logged.
// For an anonymous visitor (no class) every export is a no-op: no timer, no
// class change, no request.

import { MOTION_OFF_CLASS } from "../motion/motionSignal";

/** The class the head script puts on <html>. Literal twin of public/index.html. */
export const AUTH_BOOT_CLASS = "lf-auth-boot";

/**
 * The fade-out state class: added beside AUTH_BOOT_CLASS, it reveals #root and
 * fades the wash, glow and wordmark (CSS in public/index.html).
 */
export const AUTH_BOOT_OUT_CLASS = "lf-auth-boot-out";

/**
 * The fade's duration. Mirrors the `transition` duration of the fade-out rules
 * in public/index.html (pinned by authBoot.test.js) - change both together.
 */
export const AUTH_BOOT_FADE_MS = 200;

/**
 * How long after the fade's own duration the classes are removed. The removal
 * is timer-driven, never `transitionend`: that event does not fire when a
 * transition is skipped (a hidden tab, transitions disabled, a pseudo-element
 * the browser does not animate), and a missed event would strand the classes.
 */
const FADE_SLACK_MS = 50;

/** The latest the splash may stay up, in ms after navigation start, while React is alive. */
export const AUTH_BOOT_CAP_MS = 3000;

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const noop = () => {};

/** <html>, or null where there is no document to speak of. */
const rootElement = () => {
  try {
    return (typeof document !== "undefined" && document.documentElement) || null;
  } catch (e) {
    return null;
  }
};

/** True only while the boot class is on <html>; false wherever that cannot be read. */
const hasClass = (root, name) => {
  try {
    return Boolean(root && root.classList && root.classList.contains(name));
  } catch (e) {
    return false;
  }
};

/** Both splash classes off <html>. Never throws. */
const removeBootClasses = (root) => {
  try {
    root.classList.remove(AUTH_BOOT_CLASS, AUTH_BOOT_OUT_CLASS);
  } catch (e) {
    /* No classList: nothing was hidden through it either. */
  }
};

/** True when the OS asks for no motion. Unknown (no matchMedia) counts as motion allowed. */
const prefersReducedMotion = () => {
  try {
    return Boolean(
      typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia(REDUCED_MOTION_QUERY).matches
    );
  } catch (e) {
    return false;
  }
};

/**
 * True when the visitor asked for no motion: the OS preference, or the site's
 * own "stop animations" switch, which motion/motionSignal.js publishes as
 * `html.lf-motion-off` (the same class BrandBootLoader's pulse obeys).
 */
const motionOff = (root) => hasClass(root, MOTION_OFF_CLASS) || prefersReducedMotion();

/**
 * True while the authenticated-boot splash is up (and not already fading out).
 *
 * @returns {boolean}
 */
export function isAuthBootActive() {
  const root = rootElement();
  return hasClass(root, AUTH_BOOT_CLASS) && !hasClass(root, AUTH_BOOT_OUT_CLASS);
}

/**
 * Take the splash down: reveal #root and fade the splash out over
 * AUTH_BOOT_FADE_MS, then remove both classes. Under
 * `prefers-reduced-motion: reduce` or the site's "stop animations" switch
 * (`html.lf-motion-off`) - or when the fade cannot be scheduled - the classes
 * come off at once, with no transition.
 *
 * Idempotent and safe to call from anywhere, any number of times: a no-op when
 * the class is absent (every anonymous boot, every later navigation) or a fade
 * is already running. Never throws.
 */
export function releaseAuthBoot() {
  const root = rootElement();
  if (!isAuthBootActive()) return;

  if (motionOff(root)) {
    removeBootClasses(root);
    return;
  }

  try {
    root.classList.add(AUTH_BOOT_OUT_CLASS);
    window.setTimeout(() => removeBootClasses(root), AUTH_BOOT_FADE_MS + FADE_SLACK_MS);
  } catch (e) {
    // No timer (or no classList): never leave the page hidden waiting for one.
    removeBootClasses(root);
  }
}

/** Milliseconds since navigation start, or null when the browser cannot say. */
const sinceNavigationStart = () => {
  try {
    if (typeof performance === "undefined" || typeof performance.now !== "function") return null;
    const now = performance.now();
    return Number.isFinite(now) ? now : null;
  } catch (e) {
    return null;
  }
};

/**
 * Arm the backstop: release the splash at the latest AUTH_BOOT_CAP_MS after
 * navigation start (`performance.now()`), or AUTH_BOOT_CAP_MS from now where
 * that clock is unavailable. Already past the cap - releases on the next task
 * (a 0ms timer), never synchronously: App's mount effect runs while #root holds
 * only RootRoute's redirect, so a release right there would fade the splash
 * onto a blank page for a moment.
 *
 * Call once from App's mount effect and return the disposer as its cleanup:
 * the deadline is absolute, so a StrictMode unmount/remount re-arms the same
 * moment rather than pushing it back. A no-op (no timer at all) when the
 * splash is not up.
 *
 * @returns {() => void} clears the pending timer; never throws.
 */
export function armAuthBootCap() {
  if (!isAuthBootActive()) return noop;

  const elapsed = sinceNavigationStart();
  const delay = elapsed === null ? AUTH_BOOT_CAP_MS : Math.max(0, AUTH_BOOT_CAP_MS - elapsed);

  let timer;
  try {
    timer = window.setTimeout(releaseAuthBoot, delay);
  } catch (e) {
    // Without a timer there is no backstop to wait for - reveal now.
    releaseAuthBoot();
    return noop;
  }
  return () => {
    try {
      window.clearTimeout(timer);
    } catch (e) {
      /* Nothing to clear. */
    }
  };
}
